import React, { useEffect, useState, useMemo } from 'react';
import { fetchCostCategories, fetchNewCostQuantityTypes, addNewCost, fetchRegions } from '../services/api';
import { useRegion } from '../context/RegionContext';
import { v4 as uuidv4 } from 'uuid';
import './PageLayout.css';

function NewCostPage() {
    const { selectedRegion } = useRegion();
    
    // ✅ Initialize as empty arrays to prevent .map errors
    const [categories, setCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    
    // ✅ Add loading states
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingQuantityTypes, setLoadingQuantityTypes] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // ✅ Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const [form, setForm] = useState({
        Id: uuidv4(),
        Name: '',
        Category: '',
        QuantityType: '',
        SKU: '',
        Vendor: '',
        Value: '',
        DateAdded: getTodayDate(), // ✅ Initialize with today's date
    });

    // Filter categories to exclude EPD categories (inverse of NewCo2WeightPage)
    const nonEpdCategories = useMemo(() => {
        return categories.filter(cat => {
            // Handle null/undefined categories
            if (!cat.Categories) return false;  // ✅ Skip null/undefined entries
            return !cat.Categories.toUpperCase().includes('EPD');
        });
    }, [categories]);



    // ✅ Load available regions
    useEffect(() => {
        (async () => {
            try {
                const regions = await fetchRegions();
                console.log('🔍 NEW COST: Fetched regions:', regions);
                // ✅ Ensure it's always an array
                setAvailableRegions(Array.isArray(regions) ? regions : []);
            } catch (e) {
                console.error('Error loading regions:', e);
                setAvailableRegions([]);
            }
        })();
    }, []);

    // ✅ Get region name for display
    const regionName = useMemo(() => {
        if (!Array.isArray(availableRegions) || availableRegions.length === 0) {
            return selectedRegion;
        }
        const r = availableRegions.find((r) => r.id === selectedRegion);
        return r ? r.name : selectedRegion;
    }, [availableRegions, selectedRegion]);

    // ✅ Load categories and quantity types when region changes
    useEffect(() => {
        if (!selectedRegion) {
            console.log('🔍 NEW COST: No selectedRegion yet');
            return;
        }

        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                console.log('🔍 NEW COST: Loading categories for region:', selectedRegion);
                const categoriesData = await fetchCostCategories(selectedRegion);
                console.log("📌 Categories for region", selectedRegion, ":", categoriesData);
                
                // ✅ Ensure it's always an array
                if (Array.isArray(categoriesData)) {
                    setCategories(categoriesData);
                } else if (categoriesData && Array.isArray(categoriesData.data)) {
                    setCategories(categoriesData.data);
                } else {
                    console.warn('Categories data is not an array:', categoriesData);
                    setCategories([]);
                }
            } catch (error) {
                console.error('❌ Error fetching categories:', error);
                setCategories([]);
            } finally {
                setLoadingCategories(false);
            }
        };

        const loadQuantityTypes = async () => {
            setLoadingQuantityTypes(true);
            try {
                console.log('🔍 NEW COST: Loading quantity types for region:', selectedRegion);
                const quantityTypesData = await fetchNewCostQuantityTypes(selectedRegion);
                console.log("📌 Quantity Types for region", selectedRegion, ":", quantityTypesData);
                
                // ✅ Ensure it's always an array
                if (Array.isArray(quantityTypesData)) {
                    setQuantityTypes(quantityTypesData);
                } else if (quantityTypesData && Array.isArray(quantityTypesData.data)) {
                    setQuantityTypes(quantityTypesData.data);
                } else {
                    console.warn('Quantity types data is not an array:', quantityTypesData);
                    setQuantityTypes([]);
                }
            } catch (error) {
                console.error('❌ Error fetching quantity types:', error);
                setQuantityTypes([]);
            } finally {
                setLoadingQuantityTypes(false);
            }
        };

        loadCategories();
        loadQuantityTypes();
    }, [selectedRegion]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const generateGUID = () => {
        setForm({ ...form, Id: uuidv4() });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
    
        const formattedData = {
            name: form.Name,
            categoryName: form.Category,
            quantityTypeName: String(form.QuantityType),
            sku: form.SKU,
            vendor: form.Vendor,
            value: parseFloat(form.Value),
            dateAdded: form.DateAdded // ✅ Include date added
        };
    
        console.log("🔹 Submitting cost data for region", selectedRegion, ":", formattedData);
    
        try {
            await addNewCost(formattedData, selectedRegion);
            setShowPopup(true);
            
            setForm({
                Id: uuidv4(),
                Name: '',
                Category: '',
                QuantityType: '',
                SKU: '',
                Vendor: '',
                Value: '',
                DateAdded: getTodayDate(), // ✅ Reset to today's date
            });
        } catch (error) {
            console.error("❌ Error saving data:", error.response?.data || error);
            alert(error.response?.data?.error || 'Failed to add cost. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ✅ Add debug logging
    console.log('🔍 NEW COST RENDER STATE:', {
        selectedRegion,
        categoriesLength: categories?.length || 0,
        quantityTypesLength: quantityTypes?.length || 0,
        categoriesIsArray: Array.isArray(categories),
        quantityTypesIsArray: Array.isArray(quantityTypes),
        nonEpdCategoriesLength: nonEpdCategories?.length || 0
    });
    
    return (
        <div className="rate-builder-page">
            <div className="page-title-section">
                <h1>New Cost</h1>
                <div className="region-indicator-under-title">
                    🌏 Current Region: <strong>{regionName}</strong>
                </div>
            </div>

            {showPopup && (
                <div className="pop-up">
                    <div className="pop-up-content">
                        <h2>✅ Success</h2>
                        <p>New Cost Added to {regionName}!</p>
                        <button onClick={() => setShowPopup(false)}>OK</button>
                    </div>
                </div>
            )}
            
            <div className="form-container">
                <form onSubmit={handleSubmit}>
                    <div className="input-group">
                        <label>ID</label>
                        <input type="text" name="Id" value={form.Id} readOnly />
                        <button type="button" onClick={generateGUID}>Generate</button>
                    </div>

                    <div className="input-group">
                        <label>Name</label>
                        <input type="text" name="Name" value={form.Name} onChange={handleInputChange} required />
                    </div>

                    <div className="input-group">
                        <label>Category (Non-EPD Only)</label>
                        <select 
                            name="Category" 
                            value={form.Category} 
                            onChange={handleInputChange} 
                            required
                            disabled={submitting || loadingCategories}
                        >
                            <option value="">Select Category</option>
                            {loadingCategories ? (
                                <option disabled>Loading categories...</option>
                            ) : (
                                nonEpdCategories.length > 0 ? (
                                    nonEpdCategories.map((cat) => (
                                        <option key={cat.Id} value={cat.Id}>{cat.Categories}</option>
                                    ))
                                ) : (
                                    <option disabled>No non-EPD categories available</option>
                                )
                            )}
                        </select>
                        {!loadingCategories && nonEpdCategories.length === 0 && categories.length > 0 && (
                            <small style={{ color: '#e74c3c', marginTop: '5px', display: 'block' }}>
                                ⚠️ No non-EPD categories found. All categories contain "EPD".
                            </small>
                        )}
                    </div>
                    
                    <div className="input-group">
                        <label>Quantity Type</label>
                        <select name="QuantityType" value={form.QuantityType} onChange={handleInputChange} required disabled={submitting}>
                            <option value="">Select Quantity Type</option>
                            {loadingQuantityTypes ? (
                                <option disabled>Loading quantity types...</option>
                            ) : (
                                // ✅ Defensive check - ensure quantityTypes is an array
                                Array.isArray(quantityTypes) && quantityTypes.length > 0 ? (
                                    quantityTypes.map((qt) => (
                                        <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                    ))
                                ) : (
                                    <option disabled>No quantity types available</option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="input-group">
                        <label>SKU</label>
                        <input type="text" name="SKU" value={form.SKU} onChange={handleInputChange} required disabled={submitting} />
                    </div>

                    <div className="input-group">
                        <label>Vendor</label>
                        <input type="text" name="Vendor" value={form.Vendor} onChange={handleInputChange} required disabled={submitting} />
                    </div>

                    <div className="input-group">
                        <label>Value</label>
                        <input 
                            type="number" 
                            step="0.01"
                            name="Value" 
                            value={form.Value} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    {/* ✅ NEW: Date Added field */}
                    <div className="input-group">
                        <label>Date Added</label>
                        <input 
                            type="date" 
                            name="DateAdded" 
                            value={form.DateAdded} 
                            onChange={handleInputChange} 
                            required 
                            disabled={submitting}
                        />
                    </div>

                    <button type="submit" disabled={submitting}>
                        {submitting ? 'Adding...' : 'Add Cost'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default NewCostPage;